import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PersonalParametersSection from './PersonalParametersSection';
import FinancialParametersSection from './FinancialParametersSection';
import InvestmentStrategySection from './InvestmentStrategySection';
import AdditionalAssetsSection from './AdditionalAssetsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета финансовой независимости
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  chartData,
  setChartData,
  investmentStrategies,
  riskToleranceLevels,
  assetTypes,
  saveScenario 
}) => {
  
  // Состояние для имени сохраняемого сценария
  const [saveScenarioName, setSaveScenarioName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      currentAge, retirementAge, currentSavings, monthlyIncome, monthlyExpenses,
      expectedInflation, investmentReturn, passiveIncomeReturn, desiredPassiveIncome,
      taxRate, monthlyAdditions, savingsRate, npsAmount, npsContribution,
      realestateValue, realestateIncome
    } = formData;

    // Парсинг числовых значений
    const numCurrentAge = parseIntOrDefault(currentAge, -1);
    const numRetirementAge = parseIntOrDefault(retirementAge, -1);
    const numCurrentSavings = parseFloatOrDefault(currentSavings, -1);
    const numMonthlyIncome = parseFloatOrDefault(monthlyIncome, -1);
    const numMonthlyExpenses = parseFloatOrDefault(monthlyExpenses, -1);
    const numExpectedInflation = parseFloatOrDefault(expectedInflation, -1);
    const numInvestmentReturn = parseFloatOrDefault(investmentReturn, -1);
    const numPassiveIncomeReturn = parseFloatOrDefault(passiveIncomeReturn, -1);
    const numDesiredPassiveIncome = parseFloatOrDefault(desiredPassiveIncome, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);
    const numMonthlyAdditions = parseFloatOrDefault(monthlyAdditions, -1);
    const numSavingsRate = parseFloatOrDefault(savingsRate, -1);
    
    // Валидация базовых полей (возраст)
    if (!isInteger(currentAge) || numCurrentAge < 18 || numCurrentAge > 100) {
      newErrors.currentAge = 'Введите корректный возраст (18-100 лет).';
    }
    
    if (!isInteger(retirementAge) || numRetirementAge <= numCurrentAge || numRetirementAge > 100) {
      newErrors.retirementAge = 'Возраст выхода на пенсию должен быть больше текущего и не более 100 лет.';
    }
    
    // Валидация финансовых параметров
    if (!isPositiveNumber(currentSavings)) {
      newErrors.currentSavings = 'Текущие накопления должны быть положительным числом.';
    }
    
    if (!isPositiveNumber(monthlyIncome)) {
      newErrors.monthlyIncome = 'Ежемесячный доход должен быть положительным числом.';
    }
    
    if (!isPositiveNumber(monthlyExpenses) || numMonthlyExpenses > numMonthlyIncome) {
      newErrors.monthlyExpenses = 'Расходы должны быть положительным числом и не превышать доход.';
    }
    
    if (numExpectedInflation < 0 || numExpectedInflation > 30) {
      newErrors.expectedInflation = 'Инфляция должна быть от 0% до 30%.';
    }
    
    if (numInvestmentReturn < 0 || numInvestmentReturn > 50) {
      newErrors.investmentReturn = 'Доходность инвестиций должна быть от 0% до 50%.';
    }
    
    if (numPassiveIncomeReturn < 0 || numPassiveIncomeReturn > 30) {
      newErrors.passiveIncomeReturn = 'Доходность пассивного дохода должна быть от 0% до 30%.';
    }
    
    if (!isPositiveNumber(desiredPassiveIncome)) {
      newErrors.desiredPassiveIncome = 'Желаемый пассивный доход должен быть положительным числом.';
    }
    
    if (numTaxRate < 0 || numTaxRate > 50) {
      newErrors.taxRate = 'Ставка налога должна быть от 0% до 50%.';
    }
    
    if (numMonthlyAdditions < 0) {
      newErrors.monthlyAdditions = 'Ежемесячные пополнения не могут быть отрицательными.';
    }
    
    if (numSavingsRate < 0 || numSavingsRate > 100) {
      newErrors.savingsRate = 'Процент сбережений должен быть от 0% до 100%.';
    }
    
    // Валидация дополнительных полей
    if (formData.includeNPS) {
      if (!isPositiveNumber(npsAmount) && parseFloatOrDefault(npsAmount, 0) !== 0) {
        newErrors.npsAmount = 'Сумма в НПФ должна быть положительным числом или 0.';
      }
      
      if (!isPositiveNumber(npsContribution) && parseFloatOrDefault(npsContribution, 0) !== 0) {
        newErrors.npsContribution = 'Отчисления в НПФ должны быть положительным числом или 0.';
      }
    }
    
    if (formData.includeRealestate) {
      if (!isPositiveNumber(realestateValue) && parseFloatOrDefault(realestateValue, 0) !== 0) {
        newErrors.realestateValue = 'Стоимость недвижимости должна быть положительным числом или 0.';
      }
      
      if (!isPositiveNumber(realestateIncome) && parseFloatOrDefault(realestateIncome, 0) !== 0) {
        newErrors.realestateIncome = 'Доход от недвижимости должен быть положительным числом или 0.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev };
      
      // Для чекбоксов с массивами (выбор нескольких активов)
      if (name.startsWith('asset_')) {
        const assetId = name.replace('asset_', '');
        let consideredAssets = [...prev.consideredAssets];
        
        if (checked) {
          if (!consideredAssets.includes(assetId)) {
            consideredAssets.push(assetId);
          }
        } else {
          consideredAssets = consideredAssets.filter(id => id !== assetId);
        }
        
        newState.consideredAssets = consideredAssets;
      } else {
        // Для обычных полей
        newState[name] = fieldValue;
      }

      // Автоматическое обновление процентной ставки при изменении стратегии инвестирования
      if (name === 'investmentStrategy' && value !== 'custom') {
        const selectedStrategy = investmentStrategies.find(strategy => strategy.id === value);
        if (selectedStrategy) {
          newState.investmentReturn = selectedStrategy.expectedReturn.toString();
        }
      }

      // Автоматическое обновление ставки сбережений при изменении дохода и расходов
      if (name === 'monthlyIncome' || name === 'monthlyExpenses') {
        const income = name === 'monthlyIncome' ? parseFloatOrDefault(value, 0) : parseFloatOrDefault(prev.monthlyIncome, 0);
        const expenses = name === 'monthlyExpenses' ? parseFloatOrDefault(value, 0) : parseFloatOrDefault(prev.monthlyExpenses, 0);
        
        if (income > 0) {
          const savingsRate = Math.max(0, Math.min(100, ((income - expenses) / income) * 100));
          newState.savingsRate = savingsRate.toFixed(1);
          
          // Обновляем также ежемесячные пополнения, если пользователь не менял их вручную
          if (!prev.userChangedMonthlyAdditions) {
            newState.monthlyAdditions = Math.max(0, income - expenses).toFixed(0);
          }
        }
      }

      // Обновление ежемесячных пополнений при изменении ставки сбережений
      if (name === 'savingsRate') {
        const income = parseFloatOrDefault(prev.monthlyIncome, 0);
        const savingsRate = parseFloatOrDefault(value, 0) / 100;
        
        newState.monthlyAdditions = (income * savingsRate).toFixed(0);
        newState.userChangedMonthlyAdditions = false;
      }
      
      // Если пользователь вручную меняет ежемесячные пополнения
      if (name === 'monthlyAdditions') {
        newState.userChangedMonthlyAdditions = true;
        
        // Обновляем ставку сбережений
        const income = parseFloatOrDefault(prev.monthlyIncome, 0);
        if (income > 0) {
          const additions = parseFloatOrDefault(value, 0);
          const savingsRate = Math.min(100, (additions / income) * 100);
          newState.savingsRate = savingsRate.toFixed(1);
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeNPS' && !checked) {
        newState.npsAmount = '0';
        newState.npsContribution = '0';
      }
      
      if (name === 'includeRealestate' && !checked) {
        newState.realestateValue = '0';
        newState.realestateIncome = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [investmentStrategies, errors, setErrors, setFormData]);

  // Функция расчета
  const calculateFinancialIndependence = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setChartData([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const currentAge = parseIntOrDefault(formData.currentAge);
        const retirementAge = parseIntOrDefault(formData.retirementAge);
        const currentSavings = parseFloatOrDefault(formData.currentSavings);
        const monthlyIncome = parseFloatOrDefault(formData.monthlyIncome);
        const monthlyExpenses = parseFloatOrDefault(formData.monthlyExpenses);
        const monthlyAdditions = parseFloatOrDefault(formData.monthlyAdditions);
        const inflationRate = parseFloatOrDefault(formData.expectedInflation) / 100;
        const investmentReturn = parseFloatOrDefault(formData.investmentReturn) / 100;
        const passiveIncomeReturn = parseFloatOrDefault(formData.passiveIncomeReturn) / 100;
        const desiredPassiveIncome = parseFloatOrDefault(formData.desiredPassiveIncome);
        const taxRate = parseFloatOrDefault(formData.taxRate) / 100;
        
        // Дополнительные параметры
        const npsAmount = formData.includeNPS ? parseFloatOrDefault(formData.npsAmount) : 0;
        const npsContribution = formData.includeNPS ? parseFloatOrDefault(formData.npsContribution) : 0;
        const realestateValue = formData.includeRealestate ? parseFloatOrDefault(formData.realestateValue) : 0;
        const realestateIncome = formData.includeRealestate ? parseFloatOrDefault(formData.realestateIncome) : 0;
        
        // Учет или не учет инфляции
        const useInflationAdjusted = formData.useInflationAdjusted;
        
        // Расчет реальной доходности (за вычетом инфляции)
        const realInvestmentReturn = useInflationAdjusted 
          ? (1 + investmentReturn) / (1 + inflationRate) - 1 
          : investmentReturn;
        
        const realPassiveIncomeReturn = useInflationAdjusted 
          ? (1 + passiveIncomeReturn) / (1 + inflationRate) - 1 
          : passiveIncomeReturn;
        
        // Начальные активы с учетом дополнительных параметров
        let totalCurrentAssets = currentSavings + npsAmount + realestateValue;
        
        // Расчет ежегодных пополнений
        const yearlyAdditions = monthlyAdditions * 12;
        const yearlyNpsContribution = npsContribution * 12;
        
        // Целевой капитал для получения желаемого пассивного дохода
        // Используем формулу безопасной нормы изъятия с учетом налогов
        const targetCapital = useInflationAdjusted
          ? desiredPassiveIncome * 12 / (realPassiveIncomeReturn * (1 - taxRate))
          : desiredPassiveIncome * 12 / (passiveIncomeReturn * (1 - taxRate));
        
        // --- Выполняем расчет по годам ---
        const years = retirementAge - currentAge;
        let totalSavings = totalCurrentAssets;
        let totalTaxPaid = 0;
        let totalAdditions = 0;
        let yearlyData = [];
        
        for (let year = 0; year <= years; year++) {
          // Инфляционное изменение в разные годы
          const inflationFactor = useInflationAdjusted 
            ? Math.pow(1 + inflationRate, year) 
            : 1;
          
          // Месячный доход и расходы с учетом инфляции
          const yearlyIncomeInflated = monthlyIncome * 12 * inflationFactor;
          const yearlyExpensesInflated = monthlyExpenses * 12 * inflationFactor;
          
          // Пополнения с учетом инфляции
          const yearlyAdditionsInflated = yearlyAdditions * inflationFactor;
          const yearlyNpsContributionInflated = yearlyNpsContribution * inflationFactor;
          
          // Если это не стартовый год, добавляем пополнения и инвестиционный доход
          if (year > 0) {
            // Добавляем пополнения
            totalSavings += yearlyAdditionsInflated + yearlyNpsContributionInflated;
            totalAdditions += yearlyAdditionsInflated + yearlyNpsContributionInflated;
            
            // Рассчитываем инвестиционный доход
            const investmentIncome = totalSavings * realInvestmentReturn;
            const taxPaid = investmentIncome * taxRate;
            
            totalSavings += investmentIncome - taxPaid;
            totalTaxPaid += taxPaid;
          }
          
          // Расчет прогнозируемого пассивного дохода на текущем уровне накоплений
          const projectedPassiveIncome = totalSavings * realPassiveIncomeReturn * (1 - taxRate) / 12;
          
          // Добавляем данные года в массив для визуализации
          yearlyData.push({
            year: currentAge + year,
            totalSavings,
            totalSavingsInflationAdjusted: totalSavings / inflationFactor,
            projectedPassiveIncome,
            projectedPassiveIncomeInflationAdjusted: projectedPassiveIncome / inflationFactor,
            yearlyAdditions: yearlyAdditionsInflated,
            yearlyExpenses: yearlyExpensesInflated,
            inflationFactor
          });
          
          if (totalSavings >= targetCapital) {
            break; // Достигнута финансовая независимость
          }
        }
        
        // Определяем, достигнута ли финансовая независимость к целевому возрасту
        const isFinancialIndependenceAchieved = totalSavings >= targetCapital;
        
        // Определяем возраст достижения финансовой независимости
        const financialIndependenceAge = isFinancialIndependenceAchieved 
          ? currentAge + yearlyData.findIndex(data => data.totalSavings >= targetCapital)
          : null;
        
        // Расчет необходимых ежемесячных инвестиций для достижения цели
        let requiredMonthlyInvestment = 0;
        if (!isFinancialIndependenceAchieved) {
          // Используем приближенную формулу для расчета необходимых ежемесячных вложений
          // PMT = (FV * r) / ((1 + r)^n - 1)
          // где FV - целевая сумма, r - месячная процентная ставка, n - количество месяцев
          const monthlyRate = realInvestmentReturn / 12;
          const months = years * 12;
          const targetFuture = targetCapital - totalSavings * Math.pow(1 + realInvestmentReturn, years);
          
          requiredMonthlyInvestment = (targetFuture * monthlyRate) / (Math.pow(1 + monthlyRate, months) - 1);
          requiredMonthlyInvestment = Math.max(0, requiredMonthlyInvestment);
        }
        
        // Создаем объект с результатами
        const calculatedResults = {
          // Основные результаты
          financialIndependenceAchieved: isFinancialIndependenceAchieved,
          financialIndependenceAge,
          yearsToFI: financialIndependenceAge ? financialIndependenceAge - currentAge : null,
          targetCapital,
          totalSavingsAtRetirement: totalSavings,
          projectedPassiveIncomeAtRetirement: totalSavings * realPassiveIncomeReturn * (1 - taxRate) / 12,
          
          // Дополнительные метрики
          savingsRate: parseFloatOrDefault(formData.savingsRate) / 100,
          savingsRateRequired: requiredMonthlyInvestment / monthlyIncome,
          monthlyAdditions: parseFloatOrDefault(formData.monthlyAdditions),
          monthlyAdditionsRequired: requiredMonthlyInvestment,
          
          // Общие показатели
          totalAdditions,
          totalInvestmentReturns: totalSavings - totalCurrentAssets - totalAdditions,
          totalTaxPaid,
          
          // Анализ стратегии
          realInvestmentReturn,
          retirementSafetyMargin: (totalSavings / targetCapital - 1) * 100,
          
          // Чувствительность результатов
          inflationImpact: inflationRate > 0 ? 
            (totalSavings - (yearlyData[years]?.totalSavingsInflationAdjusted || 0)) / totalSavings * 100 : 0,
          
          // Исходные данные
          initialCapital: totalCurrentAssets,
          expectedInflation: inflationRate,
          investmentReturn,
          passiveIncomeReturn,
          taxRate,
          
          // Данные по годам для графика
          yearlyData
        };
        
        setResults(calculatedResults);
        setChartData(yearlyData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setChartData([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setChartData, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      currentAge: '30',
      retirementAge: '55',
      currentSavings: '1000000',
      monthlyIncome: '120000',
      monthlyExpenses: '80000',
      expectedInflation: '7.5',
      investmentReturn: '12',
      passiveIncomeReturn: '8',
      desiredPassiveIncome: '100000',
      taxRate: '13',
      monthlyAdditions: '30000',
      savingsRate: '25',
      riskTolerance: 'moderate',
      investmentStrategy: 'balanced',
      useInflationAdjusted: true,
      consideredAssets: ['stocks', 'bonds', 'realestate'],
      includeNPS: false,
      npsAmount: '0',
      npsContribution: '0',
      includeRealestate: true,
      realestateValue: '0',
      realestateIncome: '0',
      includeCompounding: true,
    });
    setResults(null);
    setChartData([]);
    setErrors({});
    setSaveScenarioName('');
    setShowSaveDialog(false);
  };

  // Экспорт данных в CSV
  const exportToCSV = () => {
    if (!chartData || chartData.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = [
      'Возраст',
      'Год расчета',
      'Накопления',
      'Накопления с учетом инфляции',
      'Прогнозируемый пассивный доход в месяц',
      'Пассивный доход с учетом инфляции',
      'Ежегодные пополнения',
      'Ежегодные расходы',
      'Инфляционный коэффициент'
    ];

    const formatCsvNumber = (num) => (num || 0).toFixed(2).replace('.', ',');

    const rows = chartData.map((item, index) => {
      const year = new Date().getFullYear() + index;
      return [
        item.year,
        year,
        formatCsvNumber(item.totalSavings),
        formatCsvNumber(item.totalSavingsInflationAdjusted),
        formatCsvNumber(item.projectedPassiveIncome),
        formatCsvNumber(item.projectedPassiveIncomeInflationAdjusted),
        formatCsvNumber(item.yearlyAdditions),
        formatCsvNumber(item.yearlyExpenses),
        formatCsvNumber(item.inflationFactor)
      ].join(';');
    });

    // Добавим итоговую информацию
    const summaryRows = [
      [''],
      ['Основная информация о расчете:'],
      ['Начальный капитал:', formatCsvNumber(results.initialCapital)],
      ['Целевой капитал:', formatCsvNumber(results.targetCapital)],
      ['Ежемесячные инвестиции:', formatCsvNumber(results.monthlyAdditions)],
      ['Необходимые ежемесячные инвестиции:', formatCsvNumber(results.monthlyAdditionsRequired)],
      ['Ожидаемая доходность инвестиций:', `${(results.investmentReturn * 100).toFixed(2)}%`],
      ['Ожидаемая инфляция:', `${(results.expectedInflation * 100).toFixed(2)}%`],
      ['Возраст выхода на пенсию:', formData.retirementAge],
      ['Возраст достижения финансовой независимости:', results.financialIndependenceAge || 'Не достигнуто'],
      ['Статус расчета:', results.financialIndependenceAchieved ? 'Финансовая независимость будет достигнута' : 'Финансовая независимость не будет достигнута к целевому возрасту']
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...summaryRows, '', headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'финансовая_независимость_прогноз.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения сценария
  const handleSaveScenario = () => {
    if (showSaveDialog) {
      const savedResult = saveScenario(saveScenarioName || null);
      if (savedResult) {
        alert(`Сценарий "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveScenarioName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateFinancialIndependence();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры расчета финансовой независимости
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Персональные параметры */}
        <PersonalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Финансовые параметры */}
        <FinancialParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Инвестиционная стратегия */}
        <InvestmentStrategySection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          investmentStrategies={investmentStrategies}
          riskToleranceLevels={riskToleranceLevels}
          assetTypes={assetTypes}
        />

        {/* Дополнительные активы */}
        <AdditionalAssetsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения сценария */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveScenarioName}
                onChange={(e) => setSaveScenarioName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveScenario}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать финансовую независимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {chartData.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт в CSV
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveScenario}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить сценарий
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;